import React, {useEffect, useState} from 'react';
import {func} from 'prop-types';
import {isEmpty, values} from 'lodash';

import SelectInput from '@sabre/spark-react-core/select-input';

import {NOTIFICATION_STATUS, PERCENTAGE_ABSOLUTE_TABS_VALUE} from '../../../../utils/constants.js';
import {CHART_TYPE} from '../../../../ui-components/chart-wrapper/chart-constants.js';
import {
    FARE_CHANGES_TYPES,
    getFareChangeDetailsForOnDsAndODParisConfig,
    getFareChangeForMinAvgMaxConfig,
    MIN_CHANGE_KEY
} from './chart-competition-fare-changes-config.js';
import {
    getAbsoluteOrRelativeData,
    getFareChangeDetailsForODPairs,
    getFareChangeDetailsForOnDs,
    getFareChangeForMinAvgMax
} from './chart-competition-fare-changes-util.js';
import {getLabelValuePair} from '../../app-configs-util/app-configs-util.js';
import {DASHBOARD_SEARCH_CRITERIA_KEYS, displayFromToDate} from '../charts-util.js';

import ChartPanel from '../../../../ui-components/chart-panel/chart-panel.jsx';
import GridCompetitionFareChanges from '../grid-competition-fare-changes/grid-competition-fare-changes.jsx';


const ChartCompetitionFareChanges = ({
                                         filteredFareChangesSummaryData,
                                         filterBy,
                                         searchCriteriaPayload,
                                         barOnClickHandler = () => {},
                                         legendOnClickHandler = () => {}
                                     }) => {

    const [fareChangesSummaryList, setFareChangesSummaryList] = useState([]);

    const [fareChangeDetailsOnDs, setFareChangeDetailsOnDs] = useState({transformedData: []});
    const [fareChangeDetailsODPairs, setFareChangeDetailsODPairs] = useState({transformedData: []});
    const [fareChangeDetailsMinAvgMax, setFareChangeDetailsMinAvgMax] = useState({transformedData: []});

    const [isShowGrid, setIsShowGrid] = useState(false);

    const [fareChangeDetailsOnDsConfig, setFareChangeDetailsOnDsConfig] = useState(getFareChangeDetailsForOnDsAndODParisConfig({
        isOnD: true,
        relativeAbsolute: PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE,
        filterBy
    }));
    const [fareChangeDetailsODPairsConfig, setFareChangeDetailsODPairsConfig] = useState(getFareChangeDetailsForOnDsAndODParisConfig({
        isOnD: false,
        relativeAbsolute: PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE,
        filterBy
    }));
    const [fareChangeDetailsMinAvgMaxConfig, setFareChangeDetailsMinAvgMaxConfig] = useState(getFareChangeForMinAvgMaxConfig({
        relativeAbsolute: PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE,
        filterBy
    }));

    const [relativeAbsoluteOnDs, setRelativeAbsoluteOnDs] = useState(PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE);
    const [relativeAbsoluteODPairs, setRelativeAbsoluteODPairs] = useState(PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE);
    const [relativeAbsoluteMinAvgMax, setRelativeAbsoluteMinAvgMax] = useState(PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE);

    const [pickedFareChange, setPickedFareChange] = useState('');
    const [pickedOrigin, setPickedOrigin] = useState('');
    const [pickedDestination, setPickedDestination] = useState('');

    const [cabinCode, setCabinCode] = useState('');
    const [showFaresBy, setShowFaresBy] = useState(MIN_CHANGE_KEY);
    const [inValidShowFaresBy, setInValidShowFaresBy] = useState('');

    // O&Ds bar chart - handlers
    const handleOnDsAbsoluteRelativeTabsOnClick = selectedData => setRelativeAbsoluteOnDs(selectedData);
    const handleOnDsBarChartOnClick = selectedData => {
        if (selectedData.origin) {
            setPickedDestination('');
            setCabinCode('');
            setRelativeAbsoluteODPairs(PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE);
            setRelativeAbsoluteMinAvgMax(PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE);
            setPickedOrigin(selectedData.origin);
        }
    }

    // OD Pair bar chart - handlers
    const handleODPairsAbsoluteRelativeTabsOnClick = selectedData => setRelativeAbsoluteODPairs(selectedData);
    const handleODPairsBarChartOnClick = selectedData => {
        if (selectedData.destination) {
            setPickedDestination(selectedData.destination);
        }
    }

    // line chart - handlers
    const handleMinAvgMaxAbsoluteRelativeTabsOnClick = selectedData => setRelativeAbsoluteMinAvgMax(selectedData);
    const handleMinAvgMaxCabinOnClick = selectedCabin => {
        const minAvgMax = getFareChangeForMinAvgMax(fareChangesSummaryList, pickedFareChange, pickedOrigin, pickedDestination, showFaresBy, selectedCabin, relativeAbsoluteMinAvgMax);
        setFareChangeDetailsMinAvgMax(minAvgMax);
        setCabinCode(selectedCabin);
        setFareChangeDetailsMinAvgMaxConfig(getFareChangeForMinAvgMaxConfig({
            plotLines: minAvgMax.plotLines || [],
            orgDest: `${pickedOrigin}-${pickedDestination}`,
            absoluteRelativeChangeFareForOrgDestTabsOnClickHandler: handleMinAvgMaxAbsoluteRelativeTabsOnClick,
            changeFareForOrgDestCabinOnClickHandler: handleMinAvgMaxCabinOnClick,
            relativeAbsolute: relativeAbsoluteMinAvgMax,
            cabinCodes: minAvgMax.cabinCodes,
            cabinCode: selectedCabin,
            filterBy
        }));
    }

    const handleSetShowFaresBy = selectedValue => {
        if(isEmpty(selectedValue)) {
            setInValidShowFaresBy(NOTIFICATION_STATUS.ERROR);
        } else {
            setInValidShowFaresBy('');
            setShowFaresBy(selectedValue);
            setRelativeAbsoluteODPairs(PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE);
            setRelativeAbsoluteMinAvgMax(PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE);
        }
    }

    const reDrawChart = (fcSummaryList, pickedFC) => {
        // setting data and plotting details
        const onds = getFareChangeDetailsForOnDs(fcSummaryList, `${pickedFC}Changes`);
        setFareChangeDetailsOnDs(onds);

        // setting chart configs
        setFareChangeDetailsOnDsConfig(getFareChangeDetailsForOnDsAndODParisConfig({
            isOnD: true,
            computedData: onds,
            absoluteRelativeOnClickHandler: handleOnDsAbsoluteRelativeTabsOnClick,
            handleBarChartOnClick: handleOnDsBarChartOnClick,
            relativeAbsolute: PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE,
            filterBy
        }));

        setRelativeAbsoluteOnDs(PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE);
    };

    useEffect(() => {
        if(isEmpty(pickedOrigin)) {
            return;
        }

        // setting data and plotting details
        const odPairs = getFareChangeDetailsForODPairs(fareChangesSummaryList, pickedFareChange, pickedOrigin, showFaresBy, relativeAbsoluteODPairs);
        setFareChangeDetailsODPairs(odPairs);

        // setting chart configs
        setFareChangeDetailsODPairsConfig(getFareChangeDetailsForOnDsAndODParisConfig({
            isOnD: false,
            origin: pickedOrigin,
            computedData: odPairs,
            absoluteRelativeOnClickHandler: handleODPairsAbsoluteRelativeTabsOnClick,
            handleBarChartOnClick: handleODPairsBarChartOnClick,
            relativeAbsolute: relativeAbsoluteODPairs,
            showFaresBy,
            filterBy
        }));
    }, [pickedOrigin, showFaresBy]);

    useEffect(() => {
        if(isEmpty(pickedDestination)) {
            return;
        }
        // setting data and plotting details
        const minAvgMax = getFareChangeForMinAvgMax(fareChangesSummaryList, pickedFareChange, pickedOrigin, pickedDestination, showFaresBy, null, PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE);
        setFareChangeDetailsMinAvgMax(minAvgMax);
        setCabinCode(minAvgMax.cabinCodes[0]);
        setRelativeAbsoluteMinAvgMax(PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE);

        // setting chart configs
        setFareChangeDetailsMinAvgMaxConfig(getFareChangeForMinAvgMaxConfig({
            plotLines: minAvgMax.plotLines || [],
            orgDest: `${pickedOrigin}-${pickedDestination}`,
            absoluteRelativeChangeFareForOrgDestTabsOnClickHandler: handleMinAvgMaxAbsoluteRelativeTabsOnClick,
            changeFareForOrgDestCabinOnClickHandler: handleMinAvgMaxCabinOnClick,
            relativeAbsolute: PERCENTAGE_ABSOLUTE_TABS_VALUE.ABSOLUTE,
            cabinCodes: minAvgMax.cabinCodes,
            filterBy,
            cabinCode: minAvgMax.cabinCodes[0]
        }));
    }, [pickedDestination]);

    useEffect(() => {
        if(!isEmpty(filterBy)) {
            setPickedFareChange(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT]);
        }
    }, [filterBy]);

    useEffect(() => {
        if(isEmpty(pickedFareChange)) {
            return;
        }

        setPickedOrigin('');
        setPickedDestination('');
        setIsShowGrid(false);
        setCabinCode('');
        reDrawChart(fareChangesSummaryList, pickedFareChange);
    }, [fareChangesSummaryList, pickedFareChange]);

    useEffect(() => {
        setFareChangesSummaryList(filteredFareChangesSummaryData);
    }, [filteredFareChangesSummaryData]);

    useEffect(() => {
        // setting data and plotting details
        const onds = getAbsoluteOrRelativeData(fareChangeDetailsOnDs, 'plotBars', relativeAbsoluteOnDs, {isOnDs: true});
        setFareChangeDetailsOnDs(onds);
        setFareChangeDetailsOnDsConfig(getFareChangeDetailsForOnDsAndODParisConfig({
            isOnD: true,
            computedData: onds,
            absoluteRelativeOnClickHandler: handleOnDsAbsoluteRelativeTabsOnClick,
            handleBarChartOnClick: handleOnDsBarChartOnClick,
            relativeAbsolute: relativeAbsoluteOnDs,
            filterBy
        }));
    }, [relativeAbsoluteOnDs]);

    useEffect(() => {
        // setting data and plotting details
        const odPairs = getAbsoluteOrRelativeData(fareChangeDetailsODPairs, 'plotBars', relativeAbsoluteODPairs, {oDPairs: true}, showFaresBy);
        setFareChangeDetailsODPairs(odPairs);
        setFareChangeDetailsODPairsConfig(getFareChangeDetailsForOnDsAndODParisConfig({
            isOnD: false,
            origin: (fareChangeDetailsODPairs.transformedData[0] && fareChangeDetailsODPairs.transformedData[0].origin) || '',
            computedData: odPairs,
            absoluteRelativeOnClickHandler: handleODPairsAbsoluteRelativeTabsOnClick,
            handleBarChartOnClick: handleODPairsBarChartOnClick,
            relativeAbsolute: relativeAbsoluteODPairs,
            showFaresBy,
            filterBy
        }));
    }, [relativeAbsoluteODPairs]);

    useEffect(() => {
        // setting data and plotting details
        const minAvgMax = getAbsoluteOrRelativeData(fareChangeDetailsMinAvgMax, 'plotLines', relativeAbsoluteMinAvgMax, {minAvgMax: true}, null, cabinCode);
        setFareChangeDetailsMinAvgMax(minAvgMax);
        setFareChangeDetailsMinAvgMaxConfig(getFareChangeForMinAvgMaxConfig({
            plotLines: minAvgMax.plotLines || [],
            orgDest: `${pickedOrigin}-${pickedDestination}`,
            absoluteRelativeChangeFareForOrgDestTabsOnClickHandler: handleMinAvgMaxAbsoluteRelativeTabsOnClick,
            changeFareForOrgDestCabinOnClickHandler: handleMinAvgMaxCabinOnClick,
            relativeAbsolute: relativeAbsoluteMinAvgMax,
            cabinCodes: minAvgMax.cabinCodes,
            cabinCode,
            filterBy
        }));
    }, [relativeAbsoluteMinAvgMax]);

    const postfixKey = JSON.stringify(filterBy);

    return (
        <>
            <div className='row'>
                <div className='col-sm-12 fo-spark-mar-t-4'>
                    <ChartPanel
                        key={`OnDs-${postfixKey}`}
                        chartType={CHART_TYPE.BAR_CHART_STACKED_GROUPED}
                        cssClasses={{panelCss: `chart-ods-fare-change`, panelHeaderCss: `chart-header-ods-fare-change`}}
                        isLoading={false}
                        chartData={fareChangeDetailsOnDs.transformedData}
                        configChart={fareChangeDetailsOnDsConfig}
                        apiErrorMessage={null}
                    >
                    </ChartPanel>
                </div>

                <div className='col-xs-4 col-md-3 fo-spark-mar-t-4'>
                    <SelectInput
                        key='select-fare-change-type'
                        name='select-fare-change-type'
                        options={getLabelValuePair(FARE_CHANGES_TYPES, null, true)}
                        label='Show Fares By'
                        status={inValidShowFaresBy}
                        errorMessage={'Invalid Selection'}
                        defaultValue={showFaresBy}
                        onChange={selectedValue => handleSetShowFaresBy(selectedValue)}
                    />
                </div>

                {pickedOrigin &&
                <div className='col-sm-12 spark-mar-t-2'>
                    <ChartPanel
                        key={`ODPairs-${postfixKey}`}
                        chartType={CHART_TYPE.BAR_CHART_STACKED_GROUPED}
                        cssClasses={{panelCss: `chart-fare-change`, panelHeaderCss: `chart-header-fare-change`}}
                        isLoading={false}
                        chartData={fareChangeDetailsODPairs.transformedData}
                        configChart={fareChangeDetailsODPairsConfig}
                        apiErrorMessage={null}
                    >
                    </ChartPanel>
                </div>}

                {pickedOrigin && pickedDestination && <div className='col-sm-12 fo-spark-mar-t-4'>
                    <ChartPanel
                        key={`minAvgMax-${postfixKey}`}
                        chartType={CHART_TYPE.LINE_CHART}
                        chartData={values(fareChangeDetailsMinAvgMax.transformedData)}
                        configChart={fareChangeDetailsMinAvgMaxConfig}
                    />
                </div>}

                <div className='col-sm-12 fo-spark-mar-t-4'>
                    <input
                        className='spark-btn spark-btn--xs spark-btn--primary'
                        type='button'
                        onClick={() => setIsShowGrid(!isShowGrid)}
                        value={`${isShowGrid ? 'Hide - Grid' : 'Show - Grid'}`}
                    />
                </div>
            </div>

            <div className='row fo-spark-mar-t-4'>
                {isShowGrid && <GridCompetitionFareChanges
                    key={displayFromToDate(filterBy)}
                    searchCriteriaPayload={searchCriteriaPayload}
                    fareChangesSummaryList={fareChangesSummaryList}
                    filterBy={filterBy}
                    pickedFareChange={pickedFareChange}
                    showFaresBy={showFaresBy}
                />}
            </div>
        </>
    );
};

ChartCompetitionFareChanges.propTypes = {
    barOnClickHandler: func,
    legendOnClickHandler: func
};

export default ChartCompetitionFareChanges;
